import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, nairaFormat } from "@/lib/api";
import { imgUrl } from "@/components/ImageUploader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Users } from "lucide-react";

const CATEGORY_LABELS = {
  musician: "Musicians",
  actor: "Actors",
  photographer: "Photographers",
  videographer: "Videographers",
  model: "Models",
  dancer: "Dancers",
  comedian: "Comedians",
  mc: "MCs / Hosts",
  dj: "DJs",
  influencer: "Influencers",
  designer: "Designers",
  other: "Other",
};

export default function Creatives() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (cat = active, query = q) => {
    setLoading(true);
    try {
      const params = {};
      if (cat && cat !== "all") params.category = cat;
      if (query) params.q = query;
      const [{ data: items }, { data: c }] = await Promise.all([
        api.get("/public/creatives", { params }),
        api.get("/public/categories"),
      ]);
      setList(items || []);
      setCats(c || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const t = setTimeout(() => load(active, q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [active, q]);

  return (
    <div className="min-h-screen bg-[#030A06] text-white">
      <Header />
      <section className="border-b border-[#122E1C] py-16 grain">
        <div className="max-w-7xl mx-auto px-6">
          <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#00E676]">// Creators directory</span>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter mt-3 leading-[0.95]">
            Hire vetted <span className="text-[#00E676]">Nigerian talent</span>.
          </h1>
          <p className="text-lg text-[#8A9F90] mt-6 max-w-2xl">
            Musicians, actors, photographers, hosts, dancers — every creator listed below has been verified by Nasarawa Expo. Browse, then talk to us about booking.
          </p>

          <div className="mt-10 flex gap-4 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9F90]" />
              <Input
                data-testid="creatives-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or skill…"
                className="pl-10 bg-transparent border-[#122E1C] rounded-none text-white focus:border-[#00E676] focus-visible:ring-0"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="creatives-category-tabs">
            <CatChip active={active === "all"} onClick={() => setActive("all")} label="All" count={list.length} />
            {cats.map((c) => (
              <CatChip key={c.category} active={active === c.category} onClick={() => setActive(c.category)} label={CATEGORY_LABELS[c.category] || c.category} count={c.count} testid={`cat-chip-${c.category}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12">
        {loading && <div className="text-[#8A9F90] text-center py-20">Loading creators…</div>}
        {!loading && list.length === 0 && (
          <div className="border border-dashed border-[#122E1C] p-16 text-center" data-testid="creatives-empty">
            <h3 className="font-display text-2xl">No creators listed yet</h3>
            <p className="text-[#8A9F90] mt-2 text-sm">Want to be the first? <Link to="/register" className="text-[#00E676] underline">Apply now</Link>.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#122E1C]">
          {list.map((c) => <CreativeCard key={c.id} c={c} />)}
        </div>
      </section>
    </div>
  );
}

function CatChip({ active, onClick, label, count, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-xs font-mono-jb uppercase tracking-widest border transition-colors ${
        active
          ? "bg-[#00E676] text-[#030A06] border-[#00E676]"
          : "bg-transparent text-[#8A9F90] border-[#122E1C] hover:border-[#00E676] hover:text-[#00E676]"
      }`}
    >
      {label} {count != null && <span className="opacity-70 ml-1">({count})</span>}
    </button>
  );
}

function CreativeCard({ c }) {
  const minPrice = (c.services || []).reduce((m, s) => (m == null ? s.price_ngn : Math.min(m, s.price_ngn)), null);
  return (
    <Link to={`/creatives/${c.id}`} className="block bg-[#030A06] hover:bg-[#07140B] transition-colors group" data-testid={`creative-card-${c.id}`}>
      <div className="aspect-[4/5] bg-[#07140B] relative overflow-hidden">
        {c.profile_image_path ? (
          <img src={imgUrl(c.profile_image_path)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display font-black text-7xl text-[#0D2114]">{c.name?.[0]?.toUpperCase() || "?"}</div>
        )}
        {c.verified && (
          <span className="absolute top-3 left-3 bg-[#00E676] text-[#030A06] text-[10px] font-mono-jb uppercase tracking-widest px-2 py-0.5">Verified</span>
        )}
        {c.category && (
          <span className="absolute top-3 right-3 bg-[#030A06]/80 backdrop-blur text-[#F4B41A] text-[10px] font-mono-jb uppercase tracking-widest px-2 py-0.5 border border-[#F4B41A]/40">
            {CATEGORY_LABELS[c.category] || c.category}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-xl tracking-tight">{c.name}</h3>
        {c.location && (
          <div className="text-xs text-[#8A9F90] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {c.location}</div>
        )}
        {c.bio && <p className="text-sm text-[#8A9F90] mt-2 line-clamp-2">{c.bio}</p>}
        <div className="mt-4 flex items-center justify-between">
          {minPrice != null ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">From</div>
              <div className="font-mono-jb text-[#00E676] font-bold">{nairaFormat(minPrice)}</div>
            </div>
          ) : <span className="text-xs text-[#8A9F90] font-mono-jb">Contact for rates</span>}
          {c.total_followers > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">Reach</div>
              <div className="font-mono-jb text-[#F4B41A] text-sm">{Number(c.total_followers).toLocaleString()}</div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
